import React, { useState, useEffect } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { playerAPI } from '../lib/api';
import { UserCircle, CheckCircle } from '@phosphor-icons/react';
import { toast } from 'sonner';
import rcMark from '../assets/rc-mark.png';

const USTA_RATINGS = ['3.0', '3.5', '4.0', '4.5', '5.0'];

// Post-registration "Confirm Your Profile" screen (Phase C1).
// Pre-populates every field the user already supplied on /register so they
// only review — never re-enter. Verified phone shows a green badge.
export default function CompleteProfilePage() {
  const { user, player, profileCompleted, refreshUser } = useAuth();
  const navigate = useNavigate();

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [phone, setPhone] = useState('');
  const [gender, setGender] = useState('');
  const [ustaRating, setUstaRating] = useState('');
  const [emailOptIn, setEmailOptIn] = useState(true);
  const [smsOptIn, setSmsOptIn] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  // Pre-fill everything the registration flow already collected. Prefer the
  // profile (`user`) fields — public-registration users don't have a player
  // row yet. Player fields are used as a fallback for legacy / ladder users.
  useEffect(() => {
    const src = user || {};
    const p = player || {};
    setFirstName(src.first_name || p.first_name || '');
    setLastName(src.last_name || p.last_name || '');
    setPhone(src.phone || p.phone || '');
    setGender((src.gender || '').toLowerCase());
    setUstaRating(src.usta_rating || p.usta_rating || '');
    setEmailOptIn(src.email_opt_in ?? true);
    setSmsOptIn(src.sms_opt_in ?? false);
  }, [user, player]);

  if (!user) return <Navigate to="/login" replace />;
  if (profileCompleted) return <Navigate to="/" replace />;

  // Consider the phone "verified" if the profile has a `phone_verified_at`
  // timestamp (set by /register/verify). Registration always verifies phone
  // via Twilio; invited users may not yet have this stamp.
  const phoneVerified = Boolean(user?.phone_verified_at);

  const validate = () => {
    const e = {};
    if (!firstName.trim()) e.firstName = 'Required';
    if (!lastName.trim()) e.lastName = 'Required';
    if (!gender) e.gender = 'Required';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (ev) => {
    ev.preventDefault();
    if (!validate()) return;
    setLoading(true);
    try {
      await playerAPI.updateProfile({
        first_name: firstName.trim(),
        last_name: lastName.trim(),
        phone: phone.trim() || null,
        gender: gender || null,
        usta_rating: ustaRating || null,
        email_opt_in: emailOptIn,
        sms_opt_in: smsOptIn,
      });
      await refreshUser();
      toast.success('Profile confirmed. Welcome!');
      navigate('/', { replace: true });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save profile');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#fbfaf8] flex flex-col">
      <header className="px-6 py-4 border-b border-[#e3dcd1]">
        <div className="flex items-center gap-2">
          <img src={rcMark} alt="The Rippers Club" className="w-8 h-8 object-contain" />
          <span className="font-serif text-lg text-[#013523] tracking-tight">The Rippers Club</span>
        </div>
      </header>

      <div className="flex-1 flex items-center justify-center px-6 py-10">
        <div className="w-full max-w-sm space-y-6 animate-fade-in-up" data-testid="confirm-profile-page">
          <div className="text-center">
            <div className="w-16 h-16 rounded-2xl bg-[#015e3f]/10 mx-auto flex items-center justify-center mb-4">
              <UserCircle size={32} weight="duotone" className="text-[#015e3f]" />
            </div>
            <h1 className="font-serif text-3xl text-[#013523] tracking-tight" data-testid="confirm-profile-title">
              Confirm Your Profile
            </h1>
            <p className="text-sm text-[#6a766e] mt-2">
              Please review your information and make sure everything looks correct.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-sans tracking-[0.1em] uppercase text-[#6a766e] mb-1.5 block">First Name *</label>
                <input
                  data-testid="profile-first-name"
                  type="text"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  className={`w-full bg-white border rounded-xl px-4 py-3 text-[#2a332c] focus:outline-none focus:ring-2 focus:ring-[#015e3f]/20 focus:border-[#015e3f] transition-all ${errors.firstName ? 'border-red-300' : 'border-[#e3dcd1]'}`}
                  placeholder="John"
                />
                {errors.firstName && <p className="text-[10px] text-red-500 mt-1">{errors.firstName}</p>}
              </div>
              <div>
                <label className="text-xs font-sans tracking-[0.1em] uppercase text-[#6a766e] mb-1.5 block">Last Name *</label>
                <input
                  data-testid="profile-last-name"
                  type="text"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  className={`w-full bg-white border rounded-xl px-4 py-3 text-[#2a332c] focus:outline-none focus:ring-2 focus:ring-[#015e3f]/20 focus:border-[#015e3f] transition-all ${errors.lastName ? 'border-red-300' : 'border-[#e3dcd1]'}`}
                  placeholder="Smith"
                />
                {errors.lastName && <p className="text-[10px] text-red-500 mt-1">{errors.lastName}</p>}
              </div>
            </div>

            {/* Email — read-only, always populated */}
            <div>
              <label className="text-xs font-sans tracking-[0.1em] uppercase text-[#6a766e] mb-1.5 block">Email</label>
              <input
                data-testid="profile-email"
                type="email"
                value={user?.email || ''}
                disabled
                className="w-full bg-[#f0ece3] border border-[#e3dcd1] rounded-xl px-4 py-3 text-[#6a766e] cursor-not-allowed"
              />
            </div>

            {/* Phone — populated + verified badge when applicable */}
            <div>
              <label className="text-xs font-sans tracking-[0.1em] uppercase text-[#6a766e] mb-1.5 block flex items-center justify-between">
                <span>Mobile Number</span>
                {phoneVerified && (
                  <span data-testid="profile-phone-verified" className="inline-flex items-center gap-1 text-[10px] font-medium tracking-normal normal-case text-[#015e3f]">
                    <CheckCircle size={12} weight="fill" /> Verified
                  </span>
                )}
              </label>
              <input
                data-testid="profile-phone"
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                disabled={phoneVerified}
                className={`w-full border rounded-xl px-4 py-3 text-[#2a332c] focus:outline-none focus:ring-2 focus:ring-[#015e3f]/20 focus:border-[#015e3f] transition-all ${
                  phoneVerified
                    ? 'bg-[#f0ece3] border-[#e3dcd1] text-[#6a766e] cursor-not-allowed'
                    : 'bg-white border-[#e3dcd1]'
                }`}
                placeholder="(555) 123-4567"
              />
            </div>

            {/* Gender — required, pre-filled from registration */}
            <div>
              <label className="text-xs font-sans tracking-[0.1em] uppercase text-[#6a766e] mb-1.5 block">Gender *</label>
              <div className="grid grid-cols-2 gap-2" data-testid="profile-gender">
                {['male', 'female'].map((g) => (
                  <button key={g} type="button"
                          data-testid={`profile-gender-${g}`}
                          onClick={() => setGender(g)}
                          className={`py-3 rounded-xl border text-sm font-medium transition ${
                            gender === g
                              ? 'bg-[#015e3f] text-white border-[#015e3f]'
                              : 'bg-white text-[#2a332c] border-[#e3dcd1] hover:border-[#015e3f]'
                          }`}>
                    {g === 'male' ? 'Male' : 'Female'}
                  </button>
                ))}
              </div>
              {errors.gender && <p className="text-[10px] text-red-500 mt-1">{errors.gender}</p>}
            </div>

            {/* USTA Rating — optional */}
            <div>
              <label className="text-xs font-sans tracking-[0.1em] uppercase text-[#6a766e] mb-1.5 block">USTA Rating</label>
              <select
                data-testid="profile-usta-rating"
                value={ustaRating}
                onChange={(e) => setUstaRating(e.target.value)}
                className="w-full bg-white border border-[#e3dcd1] rounded-xl px-4 py-3 text-[#2a332c] focus:outline-none focus:ring-2 focus:ring-[#015e3f]/20 focus:border-[#015e3f] transition-all appearance-none"
              >
                <option value="">Select rating...</option>
                {USTA_RATINGS.map(r => <option key={r} value={r}>{r}</option>)}
              </select>
            </div>

            <div className="space-y-3 bg-[#f0ece3] rounded-xl border border-[#e3dcd1] p-4">
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  data-testid="profile-email-opt-in"
                  type="checkbox"
                  checked={emailOptIn}
                  onChange={(e) => setEmailOptIn(e.target.checked)}
                  className="w-4 h-4 rounded border-[#e3dcd1] text-[#015e3f] focus:ring-[#015e3f]/20"
                />
                <span className="text-sm text-[#2a332c]">Email notifications</span>
              </label>
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  data-testid="profile-sms-opt-in"
                  type="checkbox"
                  checked={smsOptIn}
                  onChange={(e) => setSmsOptIn(e.target.checked)}
                  className="w-4 h-4 rounded border-[#e3dcd1] text-[#015e3f] focus:ring-[#015e3f]/20"
                />
                <span className="text-sm text-[#2a332c]">SMS notifications</span>
              </label>
            </div>

            <button
              data-testid="profile-submit-button"
              type="submit"
              disabled={loading}
              className="w-full bg-[#015e3f] text-white rounded-full px-6 py-3.5 font-medium transition-transform active:scale-95 hover:bg-[#014d34] disabled:opacity-50"
            >
              {loading ? 'Saving...' : 'Confirm & Save'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
